<template>
  <div :class="['lesson', menuStyle, browserName, { iPad: isIpadDevice }]">
    <SideMenu />
    <LessonHeader />
    <div id="content">
      <div v-if="isStudent" class="lesson-container">
        <Student :show-pdf="showPDF"></Student>
      </div>

      <div v-else-if="isTrainer" class="lesson-container">
        <Trainer :show-pdf="showPDF"></Trainer>
      </div>
    </div>
    <BottomMenu />
    <v-tour
      name="lessonPageTour"
      :steps="steps"
      v-if="enableTour"
      :options="tourOptions"
      :callbacks="tourCallbacks"
    ></v-tour>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex';

import { lessonStatus, layoutStyle, errorCodes } from '@/utils/constants';
import { redirectToHome, debounce } from '@/utils/common';
import DetectSpeed from '@/utils/detectSpeed';
import UAParser from 'ua-parser-js';

import Student from '@/components/Student';
import Trainer from '@/components/Trainer';
import LessonHeader from '@/components/Lesson/Header/LessonHeader';
import BottomMenu from '@/components/Lesson/Footer/BottomMenu';
import SideMenu from '@/components/Lesson/SideMenu';

import gracePeriodMixin from '@/mixins/gracePeriod';
import requestToken from '@/mixins/requestToken';
import vueTourMixins from '@/mixins/vueTour';

import bizTransport from '@/modules/bizTransport';

export default {
  components: {
    Student,
    Trainer,
    LessonHeader,
    BottomMenu,
    SideMenu
  },
  mixins: [gracePeriodMixin, requestToken, vueTourMixins],
  data: () => {
    return {
      nextLessonStatus: null,
      logTransportAdded: false
    };
  },
  created() {
    if (this.isAssistLesson) {
      this.switchLayoutStyle(layoutStyle.videoScreen);
    }
  },
  mounted() {
    this.setupTour('lessonPageTour');
    this.checkMediaSize();
    this.checkIfIpadDevice();
    window.addEventListener('resize', debounce(this.checkMediaSize, 50));
    window.addEventListener('orientationchange', this.setLessonHeight);
    window.addEventListener('scroll', this.setLessonHeight);

    // emit rtc disconnection when user reload/close the page
    window.onbeforeunload = () => {
      this.$logger.info('User left the lesson page', { operation: 'lesson:user-left' });
      this.socket.emit('signal', { type: 'rtc', connected: false });
      this.closedBrowser();
    };

    setInterval(() => {
      DetectSpeed.speedStatus();
    }, 300000);
  },
  beforeMount: async function () {
    if (!this.isUserTokenLoaded) {
      redirectToHome(errorCodes.ME002);
    }

    this.steps = this.isAssessmentLesson
      ? this.tourSteps[this.$i18n.locale].assessmentLesson
      : this.tourSteps[this.$i18n.locale].lesson;

    this.initialize();
  },
  computed: {
    ...mapState([
      'programId',
      'lessonStart',
      'lessonEnd',
      'lessonToken',
      'lessonStatus',
      'lessonId',
      'isTrial',
      'isTest',
      'socketToken'
    ]),
    ...mapState('material', ['pdfUrl', 'htmlContent', 'showPDFHtml']),
    ...mapState('socketio', ['socket', 'socketLessonStart']),
    ...mapState('lesson', ['isLessonBecameOngoing']),
    ...mapState('layout', ['isIpadDevice', 'isMenuVisible']),
    ...mapGetters([
      'isStudent',
      'isTrainer',
      'isAssistLesson',
      'jwtKeyValue',
      'logTransportToken',
      'isAssessmentLesson',
      'hasMaterialSupport',
      'isWarmUpPeriod'
    ]),

    isUserTokenLoaded() {
      return this.$store.state.lessonToken !== null;
    },
    showPDF() {
      return this.hasMaterialSupport && (this.pdfUrl || this.showPDFHtml);
    },
    menuStyle() {
      if (this.isMenuVisible != null) {
        return this.isMenuVisible ? 'menu-open' : 'menu-close';
      }
      return '';
    },
    browserName() {
      const browserName = new UAParser().getBrowser().name;
      if (browserName.includes('Safari')) {
        return 'safari';
      }
      return browserName.toLowerCase();
    }
  },
  methods: {
    ...mapActions('layout', ['switchLayoutStyle', 'checkMediaSize', 'checkIfIpadDevice']),
    ...mapMutations('socketio', ['setSocket', 'setLessonEventListener', 'setNewToken']),
    ...mapMutations('connection', ['setCanReconnect']),

    initialize() {
      // Attach BizTransport early so LogEventStore can capture event logs (e.g. event_type 82)
      // even before socket.io connects. LogEventStore uses GraphQL (HTTP), not socket.io,
      // so it works regardless of socket connection state.
      this.addLogTransport();

      switch (this.lessonStatus) {
        case lessonStatus.scheduled:
          this.renewTokenOnWarmUpPeriod();
          break;
        case lessonStatus.warmup:
          this.renewTokenOnLessonStart();
          break;
        case lessonStatus.ongoing:
          break;
        default:
          this.setCanReconnect(false);
          break;
      }
    },

    refreshToken: function () {
      // refresh the socket token if it's lesson status is not ongoing
      if (!this.socketLessonStart) {
        this.setNewToken(this.socketToken || this.lessonToken);
      }
      this.addLogTransport();
      this.$logger.info('Lesson status has change from scheduled to ongoing', {
        operation: 'lesson:refresh-token'
      });
    },

    addLogTransport() {
      if (this.logTransportAdded) return;
      this.logTransportAdded = true;

      const transport = new bizTransport(this.logTransportToken);
      this.$logger.add(transport);

      // If socket already connected before BizTransport was attached,
      // the original event_type 82 log was lost. Re-emit it now.
      if (this.socket?.connected) {
        this.$logger.info('Established Socket.io connection to Chat backend.', {
          operation: 'socket:connect',
          event_type: 82,
          lesson_id: this.lessonId
        });
      }
    },

    setLessonHeight(event) {
      const value = event.type === 'scroll' ? '100%' : '100vh';
      document.querySelector('.lesson').style.height = value;
    },

    closedBrowser() {
      this.$logger.info('Sending last batch of info logs', { operation: 'send-batch-log' });
    }
  },

  watch: {
    isLessonBecameOngoing: function (newVal) {
      if (newVal) {
        this.refreshToken();
      }
    },
    isWarmUpPeriod(status) {
      if (status) {
        this.addLogTransport();
      }
    }
  },

  //disconnect on close
  beforeDestroy: function () {
    this.socket.close();
    window.removeEventListener('resize', this.checkMediaSize);
    window.removeEventListener('orientationchange', this.setLessonHeight);
    window.removeEventListener('scroll', this.setLessonHeight);
    clearInterval(this.nextLessonStatus);

    this.destroyTour();
  }
};
</script>
